import React, { useState, useEffect } from "react";
import NavBar from "frontend/Components/DesignSystem/NavBar";
import HeroAnimation from "frontend/Components/HeroAnimation/HeroAnimation";
import { useNavigate } from "react-router-dom";
import "../../../css/Adviser/Pages/AdviserHomePage.css";
import "../../../css/Adviser/Pages/AdviserSupportPage.css";
import CardProduct from "frontend/Components/DesignSystem/Cards/CardProduct";
import { ReactComponent as RosemountInMediaIcon } from "../../../assets/icons/Rosemount in Media.svg";
import { ReactComponent as HearFromOurAdvisersIcon } from "../../../assets/icons/hear from our advisers.svg";
import { ReactComponent as EventsIcon } from "../../../assets/icons/Events.svg";
import FAQContainer from "frontend/Components/DesignSystem/FAQ/FAQContainer";
import { ContactUs } from "frontend/Components/ContactForm/ContactUs";
import Footer from "frontend/Components/DesignSystem/Footer";
import AdviserValues from "frontend/Components/AdviserValues/AdviserValue";
import VideoCarouselPopup from "../../Components/DesignSystem/VideoCarousel";
import ButtonYellow from "frontend/Components/DesignSystem/Buttons/ButtonYellow";
import SeoHelmet from "frontend/Components/SEO/SeoHelmet";
import { SEO_PAGES, getPageUrl } from "frontend/Utils/seoConfig";
import ImageDirectory from "frontend/HelperFunctions/ImageDirectory";
import { getAdviserValues } from "frontend/Utils/GetAdviserValues";

/**
 * Renders the Adviser Support page, providing information about the support, training, and growth opportunities available to advisers at Rosemount Financial Solutions (IFA) Ltd.
 *
 * The page includes:
 * - Navigation bar for adviser-related routes.
 * - Hero section with a headline.
 * - Case study highlighting adviser growth.
 * - Details about training and support offered.
 * - Adviser values section with a call-to-action button.
 * - Frequently asked questions (FAQs) for advisers.
 * - Service cards for media, testimonials, and events, including a modal popup for adviser videos.
 * - Contact form and footer.
 *
 * Fetches adviser values asynchronously on mount.
 *
 * @component
 * @returns {JSX.Element} The rendered Adviser Support page.
 */
const SupportPage = () => {
  const navigationItems = [
    { label: "Home", navigateTo: "/Adviser" },
    { label: "Join Us", navigateTo: "/Adviser/JoinUs" },
    { label: "About Us", navigateTo: "/Adviser/AboutUs" },
    {
      label: "Contact Us",
      navigateTo: "/ContactUs",
      state: { isAdviser: true },
    },
  ];

    const [values, setValues] = useState([]); // start with an empty array
  
    useEffect(() => {
      const fetchValues = async () => {
        const data = await getAdviserValues(); // wait for the resolved array
        setValues(data);
      };
  
      fetchValues();
    }, []);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Toggles the state of the modal between open and closed.
   * When called, it inverts the current value of `isModalOpen`.
   *
   * @function
   * @returns {void}
   */
  const handleToggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const supportFaqs = [
    {
      question: "What day to day support would I get from Rosemount?",
      answer:
        "Our support is bespoke to what you need to achieve your goals and ambitions. Daily support could include us running your social media pages, supporting you with lead generation and sales techniques, or helping you place a complex case.",
    },
    {
      question: "Is the above adviser growth example really achievable?",
      answer:
        "Yes, absolutely. Michael's story is very much true, and we have other examples of how advisers have significantly grown as part of the Rosemount network. With the right attitude, and support around you, running your own successful practice is a very real prospect.",
    },
    {
      question: "What training and development opportunities do you offer?",
      answer:
        "We regularly run CPD and training events. Starting the year with our Annual Rosemount conference. We run both financial planning and protection surgeries online every month, alongside quarterly in-person peer groups and masterclasses.",
    },
  ];

  
  return (
    <div className="Adviser-Home-Page">
      <SeoHelmet
        page={SEO_PAGES.support}
        service={{
          name: "Adviser Support",
          description:
            "Rosemount Financial Solutions provides adviser support through compliance guidance, training, business development, lead generation support, adviser events and day-to-day practical assistance.",
          serviceType: [
            "Adviser Support",
            "Mortgage Adviser Support",
            "Protection Adviser Support",
            "Financial Planning Adviser Support",
            "Compliance Support",
            "Training Support",
            "Business Growth Support",
            "Lead Generation Support",
          ],
        }}
        breadcrumbs={[
          {
            name: "Home",
            url: getPageUrl("/"),
          },
          {
            name: "Advisers",
            url: getPageUrl("/Adviser"),
          },
          {
            name: "Support",
            url: getPageUrl("/Adviser/Support"),
          },
        ]}
        faqs={supportFaqs}
      />
      <NavBar navigationItems={navigationItems} />
      <HeroAnimation
        imgSrc={ImageDirectory["support"]}
        isAdviser={true}
        altText="Two advisers speaking over paperwork and sharing a laugh."
        children={
          <>
            <h1>Supporting you every step of the way</h1>
          </>
        }
      />

      <div className="Adviser-Personal-Approach-Content">
        <h3>
          Rosemount Financial Solutions (IFA) Ltd was founded in 2006 to support
          and assist Mortgage, Protection and Financial Advisers.
        </h3>
      </div>

      <div className="Case-Study-Container">
        <div className="Case-Study-Content">
          <h2>Case Study</h2>
          <div className="Content-Large">
            Michael joined our team in June 2016, transitioning from a
            paraplanning role where his annual earnings were £30,000.
            Impressively, within a span of just 5 years, Michael progressed from
            being a novice adviser to achieving the remarkable feat of becoming
            the top producer in 2021, generating an impressive total of
            £218,000.
          </div>
          <div className="Content-Large">
            Michael’s practice, WPP, has flourished and now is boasting a team
            of 5 skilled advisers. WPP has experienced a remarkable average
            annual growth rate of 49%. Throughout its tenure of six and a half
            years with our organisation, WPP has achieved a turnover of £1.16
            million, consistently maintaining an annual average of £300,000.
            Leveraging both the comprehensive support we offer and our exposure
            to our industry expertise, we have successfully accelerated the
            earnings trajectories of many advisers, enabling individuals to
            reach their financial goals.
          </div>
        </div>
        <img
          src={ImageDirectory["WPP-growth-graphic"]}
          alt=" A graphic highlighting the growth of WPP Financial Services Limited. Specifically expanding to 5 advisers between 2017 and 2022, and growing an average of 49% per year."
        />
      </div>

      <div className="Adviser-Support-Content">
        <div className="Content-Large">
          With over two decades of experience in working with advisers, we have
          learnt our training and support must be bespoke for you. Upon joining
          our family run network, we provide training on our policies,
          procedures and systems prior to your appointment date. Once appointed,
          training is tailored to your needs.
        </div>
        <div className="Content-Large">
          We run face to face group training days throughout the year. We also
          have exclusive training plans on Protection or Equity Release.
          Training is offered for all our advisers across a the wide sector.
        </div>
      </div>
      <AdviserValues
        values={values}
        isAdviser={true}
        title="Your Partner in Success"
        children={
          <ButtonYellow
            buttonText="Join Us"
            onClick={() => navigate("/Adviser/JoinUs")}
          />
        }
      />
      <FAQContainer
        isAdviser={true}
        faqs={[
          {
            question: "What day to day support would I get from Rosemount?",
            answer:
              "Our support is bespoke to what you need to achieve your goals and ambitions. Daily support could include us running your social media pages, supporting you with lead generation and sales techniques, or helping you place a complex case.",
          },
          {
            question: "Is the above adviser growth example really achievable?",
            answer:
              "Yes, absolutely. Michael's story is very much true, and we have other examples of how advisers have significantly grown as part of the Rosemount network. With the right attitude, and support around you, running your own successful practice is a very real prospect.",
          },
          {
            question:
              "What training and development opportunities do you offer?",
            answer:
              "We regularly run CPD and training events. Starting the year with our Annual Rosemount conference. We run both financial planning and protection surgeries online every month, alongside quarterly in-person peer groups and masterclasses.",
          },
        ]}
      />
      <div className="Adviser-Services-Container Adviser-Services-Holder">
        <CardProduct
          Icon={RosemountInMediaIcon}
          title="Rosemount in the Media"
          link="/RosemountInMedia"
          isAdviser={true}
        />
        <div onClick={handleToggleModal}>
          <CardProduct
            Icon={HearFromOurAdvisersIcon}
            title="Hear from our Advisers"
            isAdviser={true}
          />
        </div>
        <VideoCarouselPopup
          isOpen={isModalOpen}
          onClose={handleToggleModal}
          // videos={videos}
        />
        <CardProduct
          Icon={EventsIcon}
          title="Events"
          link="/Adviser/Events"
          isAdviser={true}
        />
      </div>

      <ContactUs isAdviser={true} />
      <Footer isAdviser={true} />
    </div>
  );
};

export default SupportPage;
